import { ReactNode, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, Button, Box, Typography, TextField, Divider, IconButton, Tooltip,
    CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../../../context/LanguageContext';
import { useQueryBuilderData } from '../../../../context/QueryBuilderDataContext';
import { useQueryEditor } from '../../../../hooks/QueryBuilder/useQueryEditor';
import type { SavedQuery } from '../../../../hooks/QueryBuilder/useSavedQueries';
import { QUERY_NAME_MAX_LENGTH, QUERY_NAME_TAKEN, type QueryRefusal, type SaveQueryParams } from '../../../../hooks/QueryBuilder/useSaveQuery';
import SimpleQueryGroup from './SimpleQueryGroup';
import AdvancedQueryBuilder from './AdvancedQueryBuilder';
import GroupSelector from './GroupSelector';

interface EditQueryDialogProps {
    open: boolean;
    query: SavedQuery | null;
    onClose: () => void;
    // Résout au refus de l'API (nom pris, requête utilisée dans une campagne) : la fenêtre reste ouverte
    onSave: (params: SaveQueryParams) => Promise<true | QueryRefusal>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>
            {children}
        </Box>
    );
}

const noop = () => {};

export function EditQueryDialog({ open, query, onClose, onSave }: EditQueryDialogProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { simpleFields, groups: availableGroups, loadingGroups, refetchGroups } = useQueryBuilderData();
    const editor = useQueryEditor(open ? query : null);
    const { builder, description, groupId, source, usedInCampaign, criteriaChanged } = editor;
    // Une campagne cible cette requête : renommer ou changer de groupe reste possible, changer les critères passe par une copie
    const copyRequired = usedInCampaign && criteriaChanged;
    const [saving, setSaving] = useState(false);
    const [nameTaken, setNameTaken] = useState(false);

    const handleClose = () => {
        editor.reset();
        setNameTaken(false);
        onClose();
    };

    const submit = async (params: Pick<SaveQueryParams, 'queryId' | 'description'>) => {
        if (!description.trim()) {
            toast.warning(t('pages.clients.queryBuilder.provideQueryNameWarning'));
            return;
        }
        setSaving(true);
        const saved = await onSave({ ...params, groups: builder.groups, groupId, source });
        setSaving(false);
        if (saved === true) handleClose();
        // Pour une copie, c'est le nom préfixé qui est pris : le toast le précise, le champ reste valide
        else if (saved === QUERY_NAME_TAKEN && params.description === description) setNameTaken(true);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{ sx: { minHeight: fullScreen ? '100%' : '80vh' } }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{t('pages.clients.queryBuilder.editQuery')}</Typography>
                    <IconButton onClick={handleClose}><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {editor.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {usedInCampaign && (
                            <Alert severity={copyRequired ? 'warning' : 'info'} sx={{ mb: 3 }}>
                                {t('pages.clients.queryBuilder.usedInCampaignNotice')}
                            </Alert>
                        )}

                        <Section title={t('pages.clients.queryBuilder.queryNameRequired')}>
                            <TextField
                                fullWidth
                                value={description}
                                onChange={event => {
                                    editor.setDescription(event.target.value);
                                    setNameTaken(false);
                                }}
                                placeholder={t('pages.clients.queryBuilder.queryNamePlaceholder')}
                                inputProps={{ maxLength: QUERY_NAME_MAX_LENGTH }}
                                error={nameTaken}
                                helperText={nameTaken
                                    ? t('pages.clients.queryBuilder.queryNameTaken')
                                    : t('common.charCount', { count: description.length, max: QUERY_NAME_MAX_LENGTH })}
                                size="small"
                            />
                        </Section>

                        <Section title={t('pages.clients.queryBuilder.groupSelector.label')}>
                            <GroupSelector
                                selectedGroupId={groupId}
                                availableGroups={availableGroups}
                                loading={loadingGroups}
                                fullWidth
                                onGroupIdChange={editor.setGroupId}
                                onCreateGroup={noop}
                                onRefetchGroups={refetchGroups}
                            />
                        </Section>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>{t('pages.clients.queryBuilder.queryCriteria')}</Typography>

                        {source === 'SIMPLE'
                            ? <SimpleQueryGroup fields={simpleFields} rules={builder.groups[0]?.rules ?? []} onUpdateField={editor.updateSimpleField} />
                            : <AdvancedQueryBuilder builder={builder} />}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={() => submit({ description: `Copie - ${description}`.slice(0, QUERY_NAME_MAX_LENGTH) })}
                    disabled={saving}
                    sx={{ textTransform: 'none' }}
                >
                    {t('pages.clients.queryBuilder.saveCopy')}
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={handleClose} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
                    <Tooltip title={copyRequired ? t('pages.clients.queryBuilder.usedInCampaignCopyRequired') : ''}>
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={() => submit({ queryId: query?.QUERY_ID, description })}
                                disabled={saving || copyRequired}
                                sx={{ textTransform: 'none', bgcolor: 'primary.main' }}
                            >
                                {t('pages.clients.queryBuilder.modify')}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default EditQueryDialog;
